<template>
  <div>
    <b-loading :is-full-page="true" :active="!initialized"></b-loading>
    <section v-if="initialized" class="section is-main-section">
      <CardComponent>
        <div class="section has-text-centered">
          <h1 class="title">
            {{ added ? 'Create New Package' : 'Edit Package' }}
          </h1>
        </div>
        <div class="columns is-vcentered">
          <div class="column is-8 is-offset-2">
            <div class="flex-card light-bordered light-raised">
              <div class="card-body">
                <ValidationObserver ref="observer" v-slot="{ passes, errors }">
                  <form @submit.prevent="passes(submit)">
                    <div class="columns is-multiline">
                      <div class="column is-12">
                        <div class="control">
                          <ValidationProvider
                            v-slot="{ errors }"
                            name="Title"
                            rules="required"
                          >
                            <b-field
                              label="Title"
                              :type="{ 'is-danger': errors.length > 0 }"
                              :message="errors[0]"
                            >
                              <b-input v-model="item.title" />
                            </b-field>
                          </ValidationProvider>
                        </div>
                      </div>
                      <div class="column is-6-desktop">
                        <div class="control">
                          <UploadImage
                            v-model="item.image"
                            label="Image"
                            rules="required"
                          ></UploadImage>
                        </div>
                      </div>
                      <div class="column is-6">
                        <div class="control">
                          <ValidationProvider
                            v-slot="{ errors }"
                            name="Description"
                          >
                            <b-field
                              label="Description"
                              :type="{ 'is-danger': errors.length > 0 }"
                              :message="errors[0]"
                            >
                              <b-input
                                v-model="item.description"
                                type="textarea"
                              />
                            </b-field>
                          </ValidationProvider>
                        </div>
                      </div>
                      <div class="column is-6-desktop">
                        <div class="control">
                          <ValidationProvider
                            v-slot="{ errors }"
                            name="Content Type"
                            rules="required"
                          >
                            <b-field
                              label="Content Type"
                              :type="{ 'is-danger': errors.length > 0 }"
                              :message="errors[0]"
                            >
                              <b-taginput
                                v-model="item.types"
                                icon="label"
                                placeholder="Select a top-level genre"
                                autocomplete
                                :data="filteredTypes"
                                field="value"
                                :open-on-focus="true"
                                @typing="getFilteredTypes"
                              >
                              </b-taginput>
                            </b-field>
                          </ValidationProvider>
                        </div>
                      </div>
                      <div class="column is-6-desktop">
                        <div class="control">
                          <ValidationProvider
                            v-slot="{ errors }"
                            rules="required"
                            name="Genre"
                          >
                            <b-field
                              label="Genre"
                              :type="{ 'is-danger': errors.length > 0 }"
                              :message="errors[0]"
                            >
                              <b-taginput
                                v-model="item.genres"
                                icon="label"
                                placeholder="Select a genre"
                                autocomplete
                                :data="filteredGenres"
                                field="value"
                                :open-on-focus="true"
                                @typing="getFilteredGenres"
                              >
                              </b-taginput>
                            </b-field>
                          </ValidationProvider>
                        </div>
                      </div>
                      <div class="column is-6-desktop">
                        <div class="control">
                          <ValidationProvider v-slot="{ errors }" name="Tags">
                            <b-field
                              label="Tags"
                              :type="{ 'is-danger': errors.length > 0 }"
                              :message="errors[0]"
                            >
                              <b-taginput
                                v-model="item.tags"
                                icon="label"
                                placeholder="Select tags"
                                autocomplete
                                :data="filteredTags"
                                field="value"
                                :allow-new="true"
                                :open-on-focus="true"
                                @typing="getFilteredTags"
                                @add="addTag"
                              >
                              </b-taginput>
                            </b-field>
                          </ValidationProvider>
                        </div>
                      </div>
                      <div class="column is-6-desktop">
                        <div class="control has-margin-top-40">
                          <b-checkbox v-model="item.active">Active</b-checkbox>
                        </div>
                      </div>
                    </div>
                    <div class="has-text-centered">
                      <div class="is-block has-margin-bottom-15">
                        <b-button
                          icon-left="arrow-left"
                          class="is-medium has-margin-right-10"
                          tag="router-link"
                          :to="{
                            name: 'ManagePackages'
                          }"
                        >
                          Back to List</b-button
                        >
                        <b-button
                          v-if="!added"
                          class="is-medium has-margin-right-10"
                          tag="router-link"
                          :to="{
                            name: 'ManagePackageTitles',
                            params: {
                              id: item.id
                            }
                          }"
                        >
                          Manage Titles</b-button
                        >
                        <button
                          class="button is-primary is-medium btn-align no-lh raised primary-btn"
                          type="submit"
                          :disabled="loading"
                          :class="{ 'is-loading': loading }"
                        >
                          {{ added ? 'Create' : 'Update' }}
                        </button>
                      </div>
                    </div>
                  </form>
                </ValidationObserver>
              </div>
            </div>
          </div>
        </div>
      </CardComponent>
    </section>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import CardComponent from '@/components/CardComponent'
import UploadImage from '@/components/UploadImage'
import PackageApi from '@/api/packages'
import MediaApi from '@/api/media'

export default {
  name: 'EditPackage',
  components: {
    CardComponent,
    UploadImage
  },
  metaInfo: {
    title: 'Edit Package'
  },
  computed: {
    ...mapGetters(['user'])
  },
  async mounted() {
    this.added = this.$route.name === 'CreatePackage' ? true : false
    if (!this.added) {
      await this.setData()
      this.initialized = false
    }
    this.links = await MediaApi.getMetadataLinks()
    const lookups = await MediaApi.defaultLookups()
    this.genres = lookups.genre ? lookups.genre : []
    this.getFilteredGenres('')
    this.tags = lookups.tag ? lookups.tag : []
    this.types = lookups.type ? lookups.type : []
    this.filteredTags = this.tags
    this.filteredTypes = this.types
    this.initialized = true
  },
  data() {
    return {
      loading: false,
      initialized: false,
      added: false,
      item: {
        types: [],
        genres: [],
        tags: []
      },
      genres: [],
      tags: [],
      types: [],
      filteredGenres: [],
      filteredTags: [],
      filteredTypes: [],
      links: []
    }
  },
  methods: {
    ...mapActions(['getUser']),
    async setData() {
      this.initialized = false
      this.added = false
      let item = await PackageApi.getById(this.$route.params.id)
      if (item) {
        this.item.id = item.id
        this.item.title = item.title
        this.item.description = item.description
        this.item.active = item.active === 1
        this.item.image = item.image
        this.item.media_ids = item.media_ids

        if (item.packageMetadata && item.packageMetadata.length > 0) {
          this.item.genres = this.setValues(item.packageMetadata, 'genre')
          this.item.tags = this.setValues(item.packageMetadata, 'tag')
          this.item.types = this.setValues(item.packageMetadata, 'type')
        }
      }
      this.initialized = true
    },
    getValues(list) {
      return list ? list.map(item => (item.value ? item.value : item)) : []
    },
    setValues(list, type) {
      return list
        ? list
            .filter(item => item.metadata_type === type)
            .map(item => ({
              id: item.metadata_id,
              value: item.value,
              metadata_type: type
            }))
        : []
    },
    submit() {
      this.item.metadata = {
        genres: this.getValues(this.item.genres),
        tags: this.getValues(this.item.tags),
        types: this.getValues(this.item.types)
      }
      this.loading = true
      if (!this.item.id) {
        PackageApi.create(this.item)
          .then(response => {
            this.$buefy.toast.open({
              message: 'Package created successfully!',
              position: 'is-top',
              type: 'is-success'
            })
            this.$router.push({
              name: 'EditPackage',
              params: { id: response.packageId }
            })
            this.setData()
            this.loading = false
          })
          .catch(err => {
            console.log(err)
            this.$buefy.toast.open({
              message: 'Error saving package',
              position: 'is-top',
              type: 'is-danger'
            })
            this.loading = false
          })
      } else {
        this.item.packageId = this.item.id
        PackageApi.update(this.item)
          .then(() => {
            this.$buefy.toast.open({
              message: 'Package updated successfully!',
              position: 'is-top',
              type: 'is-success'
            })
            this.setData()
            this.loading = false
          })
          .catch(err => {
            console.log(err)
            this.$buefy.toast.open({
              message: 'Error saving package',
              position: 'is-top',
              type: 'is-danger'
            })
            this.loading = false
          })
      }
    },
    getFilteredMetadata(text, key, filteredKey) {
      this[filteredKey] = this[key].filter(option => {
        return option.value.toLowerCase().indexOf(text.toLowerCase()) >= 0
      })
    },
    getFilteredGenres(text) {
      const topLevelGenreIds = this.item.types.map(item => item.id)
      const childIds = this.links
        .filter(item => topLevelGenreIds.includes(item.metadata_id))
        .map(item => item.linked_metadata_id)
      this.filteredGenres = this.genres.filter(option => {
        return (
          childIds.includes(option.id) &&
          option.value.toLowerCase().indexOf(text.toLowerCase()) >= 0
        )
      })
    },
    getFilteredTags(text) {
      this.getFilteredMetadata(text, 'tags', 'filteredTags')
    },
    getFilteredTypes(text) {
      this.getFilteredMetadata(text, 'types', 'filteredTypes')
    },

    addItem(key, text) {
      if (typeof text === 'string') {
        let existing = this[key].find(
          item => item.value && item.value.toLowerCase() === text.toLowerCase()
        )
        if (existing) {
          this.item[key] = this.item[key].filter(item => item !== text)
        }
      }
    },
    addGenre(text) {
      this.addItem('genres', text)
    },
    addTag(text) {
      this.addItem('tags', text)
    },
    addType(text) {
      this.addItem('types', text)
    },
    addTopLevelGenre(mediatype) {
      let found = null
      const removeItem = mediatype === 'series' ? 'movie' : 'series'
      this.item.types = this.item.types.filter(
        item => item.value.toLowerCase() !== removeItem
      )
      found = this.types.find(item => item.value.toLowerCase() === mediatype)
      if (found) {
        this.item.types.push(found)
      }
    }
  }
}
</script>
